import { Box, BoxProps, ColorOptions, Loader, ModBox, Text, useWindowDimensions } from '@jfs/ui-new'
import { useFocusEffect, useNavigation } from '@react-navigation/core'
import React, { useEffect, useState } from 'react'
import ActionsMenu from '../../../components/ActionMenu/ActionsMenu'
import { Filters } from '../../../components/FiltersWeb'
import { filterTypes } from '../../../components/FiltersWeb/types'
import { NewTable } from '../../../components/NewTable'
import { useLazyApiData, useStores } from '../../../data/store'
import { filtersAuditData, filtersData } from '../../../utils'
import { fiuCheckerHeaderTitles, getCustomColumnWidth } from '../../../utils/constants'

const Header = ({ fetchDataOnChange, filterVisible, setFilterVisible, filterApplied }: any) => {
  return (
    <Box {...styles.header}>
      <ModBox w={'100%'}>
        <Text title={'FIU Checker Queue'} color={'BLACK_21'} fontSize={18} fontFamily={'jiotype-bold'} />
        <ActionsMenu
          fetchDataOnChange={fetchDataOnChange}
          filterVisible={filterVisible}
          setFilterVisible={setFilterVisible}
          showSearch={true}
          enableFilter
          showCalendar
          defaultPage={1}
          type="fiuGlobal"
          filterApplied={filterApplied}
        />
      </ModBox>
    </Box>
  )
}

const FiuQueue = () => {
  const {
    fetchFiuCheckerList,
    getGlobalList,
    globalCheckerListLength,
    checkSelected,
    selectedFilteredData,
    updateFilters,
    setSideMenuRefresh,
    saveState,
    resetState,
    restoreFiltersToDefaults,
    setSearchFilterBy,
    resetFeePkgId,
    resetCheckerApplicationStatus,
  } = useStores((store) => ({
    fetchFiuCheckerList: store.fetchFiuCheckerList,
    getGlobalList: store.getFiuCheckerList,
    globalCheckerListLength: store.fiuCheckerList.length,
    checkSelected: store.filter.checkSelected,
    selectedFilteredData: store.filter.getFilterData,
    updateFilters: store.filter.updateFilters,
    setSideMenuRefresh: store.setSideMenuRefresh,
    saveState: store.filter.saveState,
    resetState: store.filter.resetState,
    restoreFiltersToDefaults: store.filter.restoreFiltersToDefaults,
    applicationStatusLength: store.filter.applicationStatus.length,
    getSelectedGlobalListLength: store.getSelectedGlobalListLength(),
    setSearchFilterBy: store.onboarding.setSearchFilterBy,
    resetFeePkgId: store.onboarding.resetFeePkgId,
    resetCheckerApplicationStatus: store.resetCheckerApplicationStatus,
  }))
  const { width } = useWindowDimensions()

  const navigation = useNavigation()
  const [listData, setListData] = useState<any>([])
  const [isLoading, setLoading] = useState(false)
  const [filterVisible, setFilterVisible] = useState(false)

  const [lazyGetGlobalCheckerList, globalCheckerListHandler] = useLazyApiData(fetchFiuCheckerList)

  useEffect(() => {
    if (globalCheckerListHandler.state === 'fulfilled' || globalCheckerListHandler.state === 'rejected') {
      const list = getGlobalList()
      setListData(list)
      setLoading(false)
    }
  }, [getGlobalList, globalCheckerListHandler.state, globalCheckerListLength])

  useFocusEffect(
    React.useCallback(() => {
      setLoading(true)
      lazyGetGlobalCheckerList('', '', true)
      setSideMenuRefresh(true)
      setSearchFilterBy('Entity ID')
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  )

  const fetchDataOnChange = () => {
    console.count('===> fetchDataOnChange')
    setLoading(true)
    lazyGetGlobalCheckerList('', '', true)
  }

  const handleFilterVisibility = (visible: boolean) => {
    setFilterVisible(visible)
    if (visible) {
      saveState()
    }
  }

  const onPress = (item: any) => {
    setSearchFilterBy('Entity ID')
    resetFeePkgId()
    resetCheckerApplicationStatus()
    setSideMenuRefresh(true)
    if (item?.applicationStatus === 'Error') {
      navigation.reset({
        index: 0,
        routes: [
          {
            name: 'fiuDetails',
            params: { itemData: item, from: 'FIU_CHECKER', isApprovedByChecker: true, type: item?.auditType },
          },
        ],
      })
    } else if (item?.applicationStatus === 'Rejected') {
      navigation.reset({
        index: 0,
        routes: [
          {
            name: 'fiuDetails',
            params: {
              itemData: item,
              from: 'FIU_CHECKER',
              isApprovedByChecker: true,
              showApproveReject: false,
              type: item?.auditType,
            },
          },
        ],
      })
    } else if (item?.applicationStatus === 'Completed') {
      navigation.reset({
        index: 0,
        routes: [
          {
            name: 'fiuDetails',
            params: {
              itemData: item,
              from: 'FIU_CHECKER',
              isApprovedByChecker: false,
              showApproveReject: false,
              type: item?.auditType,
            },
          },
        ],
      })
    } else {
      navigation.reset({
        index: 0,
        routes: [
          {
            name: 'fiuDetails',
            params: {
              itemData: item,
              from: 'FIU_CHECKER',
              isApprovedByChecker: false,
              showApproveReject: true,
              type: item?.auditType,
            },
          },
        ],
      })
    }
  }

  const onFilterPress = () => {
    setFilterVisible(false)
    resetState()
  }

  const applyFilter = () => {
    fetchDataOnChange()
    setFilterVisible(false)
  }

  const restoreFilterDefaults = () => {
    restoreFiltersToDefaults()
  }

  const navigateToMyQueue = () => {
    navigation.reset({
      index: 0,
      routes: [{ name: 'fiuMakerQueue' }],
    })
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <ModBox {...styles.container} show={!isLoading}>
      <Header
        fetchDataOnChange={fetchDataOnChange}
        filterVisible={filterVisible}
        setFilterVisible={handleFilterVisibility}
        filterApplied={
          selectedFilteredData?.applicationStatus?.length > 0 &&
          !selectedFilteredData?.applicationStatus?.includes('ALL')
        }
      />
      <ModBox padding={16} show={!filterVisible}>
        <NewTable
          data={listData}
          rowHeight={48}
          headerHeight={64}
          height={544}
          customWidthFunc={getCustomColumnWidth}
          tableWidth={width > 1200 ? width - 290 : width}
          headerRowKeys={fiuCheckerHeaderTitles}
          onRowClick={onPress}
          freezeColumn={'check'}
          freezeColumnWidth={50}
        />
      </ModBox>
      <ModBox show={filterVisible}>
        <Filters
          onPress={onFilterPress}
          selectedFilteredData={selectedFilteredData}
          updateFilters={updateFilters}
          filterData={[filtersData, filtersAuditData]}
          checkSelected={checkSelected}
          apply={applyFilter}
          selectedValue={selectedFilteredData?.applicationStatus}
          mode={filterTypes?.applicationStatus}
          restoreDefaults={restoreFilterDefaults}
        />
      </ModBox>
    </ModBox>
  )
}

export default FiuQueue

export const styles = {
  container: {
    h: '100%',
    bg: 'WHITE',
    shadowRadius: 8,
    shadowColor: '#0000001F' as ColorOptions,
    borderRadius: 8,
    minHeight: 720,
    overflow: 'auto',
  } as BoxProps,
  header: {
    flexDirection: 'row',
    p: 16,
    h: 100,
    w: '100%',
    marginBottom: 16,
  } as BoxProps,
  footer: {
    p: 12,
    direction: 'row',
    justify: 'flex-end',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#0000001F' as ColorOptions,
  } as BoxProps,
}
